import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import { FoodCard } from '../components/FoodCard';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

export default function Menu() {
  const { systemConfig } = useCart();
  const [params, setParams] = useSearchParams();
  const initialCat = params.get('category') || 'All';
  const [category, setCategory] = useState(initialCat);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);

  const menuSchema = useMemo(() => {
    if (!items || items.length === 0) return null;
    const groups = {};
    items.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push({
        "@type": "MenuItem",
        "name": item.name,
        "description": item.description,
        "offers": {
          "@type": "Offer",
          "price": item.price,
          "priceCurrency": "INR"
        }
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "Menu",
      "name": "Pushpanjali Menu",
      "mainEntityOfPage": "https://pushpanjali.cloud/menu",
      "hasMenuSection": Object.entries(groups).map(([categoryName, menuItems]) => ({
        "@type": "MenuSection",
        "name": categoryName,
        "hasMenuItem": menuItems
      }))
    };
  }, [items]);


  useEffect(() => {
    setLoading(true);
    api.get('/categories').then((resCat) => {
      const activeCats = ['All', ...resCat.data.map(c => c.name)];
      setCategories(activeCats);
      if (category !== 'All' && !activeCats.includes(category)) {
        setCategory('All');
      }
    }).catch(() => {});

    api.get('/menu').then((r) => { setItems(r.data); setLoading(false); });
  }, []);

  useEffect(() => {
    if (category === 'All') params.delete('category'); else params.set('category', category);
    setParams(params, { replace: true });
  }, [category]);  // eslint-disable-line

  const filtered = useMemo(() => {
    return items.filter((i) =>
      (category === 'All' || i.category === category) &&
      (!q || i.name.toLowerCase().includes(q.toLowerCase()) || i.description.toLowerCase().includes(q.toLowerCase()))
    );
  }, [items, category, q]);

  const getBannerMessage = () => {
    if (!systemConfig) return '';
    if (systemConfig.is_online === false) {
      if (!systemConfig.allow_preorders) {
        return 'Kitchen is offline. Please check after some time.';
      } else {
        return 'Kitchen is offline but you can preorder food.';
      }
    }
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utc + (5.5 * 60 * 60000));
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[istDate.getDay()];
    const isDayOperational = systemConfig.operating_hours?.[currentDay]?.is_open;
    if (!isDayOperational) {
      const days_ordered = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const operationalDaysList = days_ordered
        .filter(day => systemConfig.operating_hours?.[day]?.is_open)
        .map(day => day.charAt(0).toUpperCase() + day.slice(1));
      return `We only serve on ${operationalDaysList.join(', ')}.`;
    }
    if (systemConfig.allow_preorders) {
      return `We are currently Closed. We are accepting pre-orders only. Next open: ${systemConfig.next_opening || 'soon'}.`;
    } else {
      return `Kitchen is Offline. Order placement is disabled. We will open again ${systemConfig.next_opening || 'soon'}.`;
    }
  };

  return (
    <div className="pb-24 md:pb-8 max-w-7xl mx-auto px-4 md:px-6 py-6">
      <SEO 
        title="Pushpanjali Menu | Order Homemade Biryani, Butter Chicken & Thalis in Noida"
        description="Explore the Pushpanjali Noida menu. Fresh starters, delicious home-cooked chicken curry, rich butter chicken, healthy thalis & warm desserts delivered fresh."
        keywords="pushpanjali menu Noida, order biryani noida, homemade butter chicken, food delivery noida, homemade thali noida, ghar ka khana menu"
        schema={menuSchema}
      />
      {!systemConfig?.is_currently_open && (
        <div className={`mb-6 py-3 px-4 rounded-2xl text-center text-xs md:text-sm font-semibold flex items-center justify-center gap-2 border leading-snug ${
          systemConfig?.allow_preorders 
            ? 'bg-amber-50 text-amber-800 border-amber-200' 
            : 'bg-red-50 text-red-900 border-red-200'
        }`}>
          <Clock className="w-4 h-4 shrink-0" />
          <span>{getBannerMessage()}</span>
        </div>
      )}
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-textprimary mb-1">Explore the Menu</h1>
      <p className="text-textsecondary font-body text-sm mb-6">Handcrafted dishes, made with love</p>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-textlight" />
        <input
          data-testid="menu-search-input"
          type="text"
          placeholder="Search for biryani, curry, dessert..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white rounded-2xl border border-borderbeige focus:outline-none focus:ring-2 focus:ring-saffron/40 font-body text-sm shadow-card"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-6 -mx-1 px-1">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`filter-${c.toLowerCase()}`}
            className={`shrink-0 px-4 py-2 rounded-full font-heading font-semibold text-sm transition-all ${
              category === c
                ? 'bg-saffron text-white shadow-warm'
                : 'bg-white text-textprimary border border-borderbeige hover:border-saffron'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-textsecondary font-body">Loading delicious food...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-textsecondary font-body">No dishes match your search.</div>
      ) : (
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {filtered.map((item) => (
            <FoodCard key={item.id} item={item} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
